import { useState, useRef } from "react";
import { X, Upload, FileText, Trash2 } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../ui/select";

interface AddDocumentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave?: (data: any) => void;
}

const documentCategories = [
  { id: "form", name: "Form" },
  { id: "id", name: "ID Document" },
  { id: "medical", name: "Medical Record" },
  { id: "prescription", name: "Prescription" },
  { id: "referral", name: "Referral Letter" },
  { id: "other", name: "Other" },
];

export default function AddDocumentModal({
  isOpen,
  onClose,
  onSave,
}: AddDocumentModalProps) {
  const [documentName, setDocumentName] = useState("");
  const [category, setCategory] = useState("");
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [notes, setNotes] = useState("");

  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setUploadedFile(file);
      if (!documentName) {
        setDocumentName(file.name.replace(/\.[^/.]+$/, ""));
      }
    }
  };

  const removeFile = () => {
    setUploadedFile(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleSave = () => {
    const data = {
      documentName,
      category,
      file: uploadedFile,
      notes,
    };
    onSave?.(data);
    // Reset form
    setDocumentName("");
    setCategory("");
    setUploadedFile(null);
    setNotes("");
    onClose();
  };

  const isValid = documentName && category;

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md overflow-hidden border border-purple-100">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <h2 className="text-lg font-bold text-[#2d2d2d]">Add Document</h2>
          <button
            onClick={onClose}
            className="p-1.5 hover:bg-gray-100 rounded-lg transition-colors text-gray-400 hover:text-gray-600"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="px-5 py-4 space-y-5">
          {/* Document Name */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Document Name
            </label>
            <input
              type="text"
              value={documentName}
              onChange={(e) => setDocumentName(e.target.value)}
              placeholder="Enter document name..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
          </div>

          {/* Category */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Category
            </label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full border-gray-200 h-[42px] text-sm">
                <SelectValue placeholder="Select category..." />
              </SelectTrigger>
              <SelectContent className="bg-white border border-gray-200 shadow-xl rounded-xl z-[1200]">
                {documentCategories.map((cat) => (
                  <SelectItem key={cat.id} value={cat.id}>
                    {cat.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* File Upload */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Upload File
            </label>
            {uploadedFile ? (
              <div className="flex items-center justify-between p-3 border border-[#e8e0f0] rounded-lg bg-purple-50/50">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-[#704aaf] rounded-lg flex items-center justify-center">
                    <FileText className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#3d3d3d]">{uploadedFile.name}</p>
                    <p className="text-xs text-[#6b6b6b]">
                      {(uploadedFile.size / 1024).toFixed(1)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={removeFile}
                  className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg transition-colors"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed border-[#e8e0f0] rounded-lg p-6 flex flex-col items-center justify-center cursor-pointer hover:border-[#704aaf] hover:bg-purple-50/30 transition-colors"
              >
                <Upload className="w-8 h-8 text-[#704aaf] mb-2" />
                <p className="text-sm text-[#6b6b6b]">Click to upload file</p>
                <p className="text-xs text-[#9b9b9b]">PDF, DOC, JPG up to 10MB</p>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
              onChange={handleFileUpload}
              className="hidden"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-[#2d2d2d] mb-2">
              Notes (Optional)
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any notes about this document..."
              className="w-full px-3 py-2.5 border border-gray-200 rounded-lg text-sm text-[#3d3d3d] placeholder-gray-400 resize-none h-20 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-purple-300 transition-all"
            />
          </div>
        </div>

        {/* Footer */}
        <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50">
          <div className="flex items-center justify-end gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Cancel
            </button>
            <button
              onClick={handleSave}
              disabled={!isValid}
              className="flex items-center gap-2 px-4 py-2 bg-[#704aaf] text-white text-sm font-medium rounded-lg hover:bg-[#5f3d9a] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <FileText className="w-4 h-4" />
              Save Document
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

